import { useQuery } from '@tanstack/react-query';
import { ApiClient, CompanionDevice, ExternalLink, Group, Member, OmpMessage, OmpSiteLink, OmpLinkStatus, Site as SiteSchema, UnmatchedPerson, page } from '@cloudsign/shared';
import type { Site } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { useCapabilitiesQuery } from '../../lib/session';

export const ClientList = z.array(ApiClient);
export const DeviceList = z.array(CompanionDevice);
export const UnmatchedList = z.array(UnmatchedPerson);
export const LinkList = z.array(ExternalLink);
export const GroupList = z.array(Group);
export const SitePage = page(SiteSchema);
export const MemberPage = page(Member);
export const SiteLinkList = z.array(OmpSiteLink);
export const MessageList = z.array(OmpMessage);
export const RotateResult = z.object({ secret: z.string() });
export { OmpLinkStatus };

export const clientsKey = ['api-clients', 'list'] as const;
export const unmatchedKey = (system: string) => ['api-clients', 'unmatched', system] as const;
export const devicesKey = (memberId: string) => ['companion', 'devices', memberId] as const;
export const ompKey = ['omp-link'] as const;

/** True when the tenant has the module switched on (the server still checks every call). */
export function useModuleOn(key: string): boolean {
  return useCapabilitiesQuery().data?.modules.some((m) => m.key === key && m.enabled) ?? false;
}

export function useClients() {
  return useQuery({ queryKey: clientsKey, queryFn: () => api.get('/api-clients', { schema: ClientList }) });
}

export function useSites() {
  return useQuery({
    queryKey: ['people', 'sites'],
    queryFn: () => api.get('/sites', { query: { status: 'active', pageSize: 200 }, schema: SitePage }).then((r): Site[] => r.items),
    staleTime: 60_000,
  });
}

export function useGroups() {
  return useQuery({ queryKey: ['people', 'groups'], queryFn: () => api.get('/groups', { query: { status: 'active' }, schema: GroupList }), staleTime: 60_000 });
}

export function useUnmatched(system: string) {
  return useQuery({
    queryKey: unmatchedKey(system),
    enabled: system !== '',
    queryFn: () => api.get('/api-clients/unmatched', { query: { system }, schema: UnmatchedList }),
  });
}
